import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import {
    Camera,
    Printer,
    MonitorSmartphone,
    CalendarClock,
    Image as ImagesIcon,
    Users,
    History as HistoryIcon,
    RefreshCw,
    Power,
    PlugZap,
    Wifi,
    WifiOff,
    Check,
    ChevronsUpDown,
} from 'lucide-react';
import { Button } from '../../shadComponents/ui/button';
import { Input } from '../../shadComponents/ui/input';
import { Badge } from '../../shadComponents/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../shadComponents/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../../shadComponents/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../../shadComponents/ui/command';
import useRemoteControl, {
    DeviceCommand,
    DevicePhoto,
    RemoteSession,
} from '../hooks/useRemoteControl';
import RuntimeVariables from './RuntimeVariables';

type RouteParams = {
    [key: string]: string | undefined;
    orgId: string;
    deviceKeyId: string;
};

const CAMERA_SETTINGS: { key: string; label: string }[] = [
    { key: 'iso', label: 'ISO' },
    { key: 'aperture', label: 'Aperture' },
    { key: 'shutterSpeed', label: 'Shutter' },
    { key: 'whiteBalance', label: 'White Bal.' },
];

/** Compact section shell used across the panel. */
const Panel: React.FC<{
    icon: React.ReactNode;
    title: string;
    action?: React.ReactNode;
    children: React.ReactNode;
    className?: string;
}> = ({ icon, title, action, children, className }) => (
    <section
        className={`rounded-xl border bg-card text-card-foreground shadow-sm ${
            className || ''
        }`}
    >
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
            <div className="flex items-center gap-2 text-sm font-semibold">
                <span className="text-muted-foreground">{icon}</span>
                {title}
            </div>
            {action}
        </div>
        <div className="p-4">{children}</div>
    </section>
);

export const RemoteControlView: React.FC = () => {
    const { orgId, deviceKeyId } = useParams<RouteParams>();

    const {
        connected,
        connecting,
        state,
        sessions,
        history,
        photos,
        connect,
        disconnect,
        endSession,
        sendCommand,
    } = useRemoteControl(Number(orgId), deviceKeyId || '');

    const [copies, setCopies] = useState(1);

    const currentState = state?.state || {};
    const capabilities = state?.capabilities || {};
    const cameraState = (currentState.camera || {}) as Record<string, any>;
    const printerState = (currentState.printer || {}) as Record<string, any>;
    const activationState = (currentState.activation || {}) as Record<
        string,
        any
    >;
    const cameraCaps = (capabilities.camera || {}) as Record<string, string[]>;
    const activationCaps = (capabilities.activations || []) as {
        id: number;
        name: string;
        date?: string;
    }[];
    const printerCaps = (capabilities.printers || []) as {
        name: string;
        uri?: string;
    }[];

    const disabled = !connected;

    // Consider the device unresponsive if connected but no fresh report.
    const reportedAgeSec = state?.reportedAt
        ? moment().diff(moment(state.reportedAt), 'seconds')
        : null;
    const stale = connected && reportedAgeSec !== null && reportedAgeSec > 45;

    const send = async (
        commandType: string,
        payload?: Record<string, any>,
        successMessage?: string,
    ) => {
        const result = await sendCommand(commandType, payload);
        if (result) {
            toast.success(successMessage || `Sent ${commandType}`);
        } else {
            toast.error(`Failed to send ${commandType}`);
        }
    };

    return (
        <div className="mx-auto max-w-6xl space-y-4 p-4">
            {/* Sticky header */}
            <div className="sticky top-0 z-10 -mx-4 flex flex-wrap items-center justify-between gap-3 border-b bg-background/80 px-4 py-3 backdrop-blur">
                <div className="flex items-center gap-3">
                    <MonitorSmartphone className="h-5 w-5" />
                    <div>
                        <div className="text-base font-semibold leading-tight">
                            Remote Control
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                            {deviceKeyId}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <StatusPill
                        connecting={connecting}
                        connected={connected}
                        stale={stale}
                    />
                    {state?.reportedAt && (
                        <span className="hidden text-xs text-muted-foreground sm:inline">
                            updated {moment(state.reportedAt).fromNow()}
                        </span>
                    )}
                    {connected ? (
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={disconnect}
                        >
                            <Power className="mr-1.5 h-4 w-4" /> Disconnect
                        </Button>
                    ) : (
                        <Button
                            size="sm"
                            onClick={connect}
                            disabled={connecting}
                        >
                            <PlugZap className="mr-1.5 h-4 w-4" /> Connect
                        </Button>
                    )}
                </div>
            </div>

            {!connected && (
                <div className="rounded-lg border border-dashed bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground">
                    Connect to start a remote-control session. The device ramps
                    up its polling while you are connected.
                </div>
            )}

            {/* Top row: screen + sessions */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <ScreenSnapshot
                    className="lg:col-span-2"
                    snapshotUrl={state?.snapshotUrl || null}
                    reportedAt={state?.reportedAt}
                    disabled={disabled}
                    onCapture={() =>
                        send('capture_screenshot', undefined, 'Capturing screen')
                    }
                />
                <ActiveSessions sessions={sessions} onEnd={endSession} />
            </div>

            {/* Controls row */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <CameraControls
                    cameraState={cameraState}
                    cameraCaps={cameraCaps}
                    disabled={disabled}
                    onChange={(key, value) =>
                        send(
                            'set_camera_setting',
                            { key, value },
                            `Set ${key}`,
                        )
                    }
                />
                <PrinterControls
                    printerState={printerState}
                    printerCaps={printerCaps}
                    disabled={disabled}
                    onSelectPrinter={(name, uri) =>
                        send(
                            'select_printer',
                            { printerName: name, printerId: uri },
                            `Selected ${name}`,
                        )
                    }
                    onSetMargins={(margins) =>
                        send(
                            'set_printer_margins',
                            margins,
                            'Updated margins',
                        )
                    }
                />
                <ActivationControls
                    activationState={activationState}
                    activations={activationCaps}
                    disabled={disabled}
                    onSelect={(activationId) =>
                        send(
                            'select_activation',
                            { activationId },
                            'Navigating to event',
                        )
                    }
                    onRefresh={() =>
                        send('refresh_activation', undefined, 'Refreshing')
                    }
                />
            </div>

            <Panel icon={<Power className="h-4 w-4" />} title="Runtime Variables">
                <RuntimeVariables
                    runtime={currentState.runtime}
                    disabled={disabled || stale}
                    onSet={async (instanceId, key, value) => {
                        const result = await sendCommand('set_runtime_variable', { instanceId, key, value });
                        if (result) toast.success(`Queued change to ${key}`);
                        else toast.error(`Could not queue change to ${key}`);
                        return !!result;
                    }}
                />
            </Panel>

            {/* Photo gallery */}
            <PhotoGallery
                photos={photos}
                copies={copies}
                onCopiesChange={setCopies}
                disabled={disabled}
                onPrint={(shareIdentifier) =>
                    send(
                        'issue_print',
                        { shareIdentifier, copies },
                        `Printing ${copies} ${copies > 1 ? 'copies' : 'copy'}`,
                    )
                }
            />

            <ChangeHistory history={history} />
        </div>
    );
};

const StatusPill: React.FC<{
    connecting: boolean;
    connected: boolean;
    stale: boolean;
}> = ({ connecting, connected, stale }) => {
    if (connecting) return <Badge variant="secondary">Connecting…</Badge>;
    if (connected && stale)
        return (
            <Badge variant="destructive">
                <WifiOff className="mr-1 h-3 w-3" /> Unresponsive
            </Badge>
        );
    if (connected)
        return (
            <Badge variant="default">
                <Wifi className="mr-1 h-3 w-3" /> Connected
            </Badge>
        );
    return <Badge variant="outline">Idle</Badge>;
};

const ScreenSnapshot: React.FC<{
    className?: string;
    snapshotUrl: string | null;
    reportedAt?: string;
    disabled: boolean;
    onCapture: () => void;
}> = ({ className, snapshotUrl, reportedAt, disabled, onCapture }) => (
    <Panel
        className={className}
        icon={<MonitorSmartphone className="h-4 w-4" />}
        title="Screen"
        action={
            <Button
                size="sm"
                variant="outline"
                onClick={onCapture}
                disabled={disabled}
            >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Capture
            </Button>
        }
    >
        {snapshotUrl ? (
            <div className="space-y-2">
                <img
                    src={snapshotUrl}
                    alt="Booth screen"
                    className="max-h-[360px] w-full rounded-lg border object-contain bg-muted"
                />
                {reportedAt && (
                    <div className="text-xs text-muted-foreground">
                        Captured {moment(reportedAt).format('MMM D, h:mm:ss A')}
                    </div>
                )}
            </div>
        ) : (
            <div className="flex h-40 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                No snapshot yet
            </div>
        )}
    </Panel>
);

const ActiveSessions: React.FC<{
    sessions: RemoteSession[];
    onEnd: (id: number) => void;
}> = ({ sessions, onEnd }) => (
    <Panel
        icon={<Users className="h-4 w-4" />}
        title={`Active Sessions (${sessions.length})`}
    >
        {sessions.length === 0 ? (
            <div className="text-sm text-muted-foreground">
                No active sessions.
            </div>
        ) : (
            <div className="space-y-2">
                {sessions.map((session) => (
                    <div
                        key={session.id}
                        className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2"
                    >
                        <div className="min-w-0">
                            <div className="truncate text-sm font-medium">
                                {session.startedByUsername ||
                                    session.startedByEmail ||
                                    `Account ${session.startedBy ?? '?'}`}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {moment(session.startedAt).fromNow()}
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEnd(session.id)}
                        >
                            End
                        </Button>
                    </div>
                ))}
            </div>
        )}
    </Panel>
);

const CameraControls: React.FC<{
    cameraState: Record<string, any>;
    cameraCaps: Record<string, string[]>;
    disabled: boolean;
    onChange: (key: string, value: string) => void;
}> = ({ cameraState, cameraCaps, disabled, onChange }) => {
    const hasCaps = Object.values(cameraCaps).some(
        (values) => Array.isArray(values) && values.length > 0,
    );
    const subtitle =
        cameraState.cameraType === 'external'
            ? cameraState.model || 'External camera'
            : cameraState.cameraType === 'internal'
            ? 'Built-in (read-only)'
            : 'No camera';

    return (
        <Panel icon={<Camera className="h-4 w-4" />} title="Camera">
            <div className="mb-3 truncate text-xs text-muted-foreground">
                {subtitle}
            </div>
            {!hasCaps ? (
                <div className="text-sm text-muted-foreground">
                    No adjustable settings (external cameras only).
                </div>
            ) : (
                <div className="space-y-2.5">
                    {CAMERA_SETTINGS.map(({ key, label }) => {
                        const options = cameraCaps[key] || [];
                        if (options.length === 0) return null;
                        return (
                            <div
                                key={key}
                                className="flex items-center justify-between gap-3"
                            >
                                <label className="w-20 shrink-0 text-xs font-medium text-muted-foreground">
                                    {label}
                                </label>
                                <Select
                                    value={cameraState[key] || undefined}
                                    onValueChange={(value) =>
                                        onChange(key, value)
                                    }
                                    disabled={disabled}
                                >
                                    <SelectTrigger className="h-8 flex-1">
                                        <SelectValue
                                            placeholder={
                                                cameraState[key] || 'Select'
                                            }
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {options.map((option) => (
                                            <SelectItem
                                                key={option}
                                                value={option}
                                            >
                                                {option}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        );
                    })}
                </div>
            )}
        </Panel>
    );
};

const PrinterControls: React.FC<{
    printerState: Record<string, any>;
    printerCaps: { name: string; uri?: string }[];
    disabled: boolean;
    onSelectPrinter: (name: string, uri: string) => void;
    onSetMargins: (margins: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    }) => void;
}> = ({ printerState, printerCaps, disabled, onSelectPrinter, onSetMargins }) => {
    const [margins, setMargins] = useState({
        top: Number(printerState.topMargin ?? 0),
        bottom: Number(printerState.bottomMargin ?? 0),
        left: Number(printerState.leftMargin ?? 0),
        right: Number(printerState.rightMargin ?? 0),
    });

    React.useEffect(() => {
        setMargins({
            top: Number(printerState.topMargin ?? 0),
            bottom: Number(printerState.bottomMargin ?? 0),
            left: Number(printerState.leftMargin ?? 0),
            right: Number(printerState.rightMargin ?? 0),
        });
    }, [
        printerState.topMargin,
        printerState.bottomMargin,
        printerState.leftMargin,
        printerState.rightMargin,
    ]);

    return (
        <Panel icon={<Printer className="h-4 w-4" />} title="Printer">
            <div className="space-y-3">
                <Select
                    value={printerState.name || undefined}
                    onValueChange={(name) => {
                        const printer = printerCaps.find((p) => p.name === name);
                        onSelectPrinter(name, printer?.uri || '');
                    }}
                    disabled={disabled}
                >
                    <SelectTrigger className="h-8">
                        <SelectValue
                            placeholder={printerState.name || 'Select printer'}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        {printerCaps.length === 0 ? (
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                No printers found
                            </div>
                        ) : (
                            printerCaps.map((printer) => (
                                <SelectItem
                                    key={printer.name}
                                    value={printer.name}
                                >
                                    {printer.name}
                                </SelectItem>
                            ))
                        )}
                    </SelectContent>
                </Select>

                <div className="grid grid-cols-2 gap-2">
                    {(['top', 'bottom', 'left', 'right'] as const).map((edge) => (
                        <div key={edge} className="flex items-center gap-2">
                            <label className="w-12 shrink-0 text-xs capitalize text-muted-foreground">
                                {edge}
                            </label>
                            <Input
                                type="number"
                                min={0}
                                max={30}
                                className="h-8"
                                value={margins[edge]}
                                disabled={disabled}
                                onChange={(e) =>
                                    setMargins((prev) => ({
                                        ...prev,
                                        [edge]: Number(e.target.value),
                                    }))
                                }
                            />
                        </div>
                    ))}
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    disabled={disabled}
                    onClick={() => onSetMargins(margins)}
                >
                    Apply margins
                </Button>
            </div>
        </Panel>
    );
};

const ActivationControls: React.FC<{
    activationState: Record<string, any>;
    activations: { id: number; name: string; date?: string }[];
    disabled: boolean;
    onSelect: (activationId: number) => void;
    onRefresh: () => void;
}> = ({ activationState, activations, disabled, onSelect, onRefresh }) => {
    // Most recent first: by event date when available, else by id (newer ids
    // are more recent), so the latest events surface at the top of the list.
    const sorted = React.useMemo(
        () =>
            [...activations].sort((a, b) => {
                const da = a.date ? Date.parse(a.date) : NaN;
                const db = b.date ? Date.parse(b.date) : NaN;
                if (!isNaN(da) && !isNaN(db)) return db - da;
                return (b.id || 0) - (a.id || 0);
            }),
        [activations],
    );

    return (
        <Panel icon={<CalendarClock className="h-4 w-4" />} title="Activation">
            <div className="space-y-3">
                <div className="truncate text-xs text-muted-foreground">
                    Current: {activationState.name || 'None'}
                </div>
                <SearchableActivationSelect
                    activations={sorted}
                    selectedId={
                        activationState.id ? Number(activationState.id) : null
                    }
                    disabled={disabled}
                    onSelect={onSelect}
                />
                <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    disabled={disabled}
                    onClick={onRefresh}
                >
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh &amp;
                    restart
                </Button>
            </div>
        </Panel>
    );
};

const SearchableActivationSelect: React.FC<{
    activations: { id: number; name: string; date?: string }[];
    selectedId: number | null;
    disabled: boolean;
    onSelect: (activationId: number) => void;
}> = ({ activations, selectedId, disabled, onSelect }) => {
    const [open, setOpen] = useState(false);
    const selected = activations.find((a) => a.id === selectedId);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className="h-8 w-full justify-between font-normal"
                >
                    <span className="truncate">
                        {selected ? selected.name : 'Select an event'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                    <CommandInput placeholder="Search events..." />
                    <CommandList>
                        <CommandEmpty>No events found.</CommandEmpty>
                        <CommandGroup>
                            {activations.map((activation) => (
                                <CommandItem
                                    key={activation.id}
                                    // cmdk filters on this value; include id so
                                    // searching by name (or id) works.
                                    value={`${activation.name} ${activation.id}`}
                                    onSelect={() => {
                                        onSelect(activation.id);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={`mr-2 h-4 w-4 ${
                                            activation.id === selectedId
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        }`}
                                    />
                                    <div className="flex min-w-0 flex-col">
                                        <span className="truncate">
                                            {activation.name}
                                        </span>
                                        {activation.date && (
                                            <span className="text-xs text-muted-foreground">
                                                {moment(activation.date).format(
                                                    'MMM D, YYYY',
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
};

const PhotoGallery: React.FC<{
    photos: DevicePhoto[];
    copies: number;
    onCopiesChange: (n: number) => void;
    disabled: boolean;
    onPrint: (shareIdentifier: string) => void;
}> = ({ photos, copies, onCopiesChange, disabled, onPrint }) => (
    <Panel
        icon={<ImagesIcon className="h-4 w-4" />}
        title={`Photos (${photos.length})`}
        action={
            <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground">Copies</label>
                <Input
                    type="number"
                    min={1}
                    max={20}
                    className="h-8 w-16"
                    value={copies}
                    disabled={disabled}
                    onChange={(e) =>
                        onCopiesChange(Math.max(1, Number(e.target.value)))
                    }
                />
            </div>
        }
    >
        {photos.length === 0 ? (
            <div className="flex h-28 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                No photos uploaded by this device yet
            </div>
        ) : (
            <div className="grid max-h-[400px] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4 md:grid-cols-6">
                {photos.map((photo) => {
                    const src =
                        photo.thumbnail800pxUri ||
                        photo.thumbnailUri ||
                        photo.thumbnailTinyUri ||
                        photo.originalUri ||
                        '';
                    return (
                        <button
                            key={photo.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => onPrint(photo.shareIdentifier)}
                            className="group relative aspect-square overflow-hidden rounded-lg border bg-muted transition hover:ring-2 hover:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                            title="Click to print"
                        >
                            {src ? (
                                <img
                                    src={src}
                                    alt={photo.shareIdentifier}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                                    no preview
                                </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition group-hover:opacity-100">
                                <span className="flex items-center gap-1 text-xs font-medium text-white">
                                    <Printer className="h-4 w-4" /> Print
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>
        )}
    </Panel>
);

const HISTORY_STATUS_COLOR: Record<string, string> = {
    acked: 'text-emerald-400',
    pending: 'text-amber-400',
    sent: 'text-sky-400',
    failed: 'text-red-400',
    expired: 'text-zinc-500',
};

const ChangeHistory: React.FC<{ history: DeviceCommand[] }> = ({ history }) => (
    <Panel icon={<HistoryIcon className="h-4 w-4" />} title="Change History">
        <div className="max-h-[320px] overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs leading-relaxed text-zinc-300">
            {history.length === 0 ? (
                <div className="text-zinc-500">No commands yet.</div>
            ) : (
                history.map((command) => {
                    const detail = command.error
                        ? command.error
                        : command.payload
                        ? JSON.stringify(command.payload)
                        : '';
                    return (
                        <div
                            key={command.id}
                            className="whitespace-pre-wrap break-words border-b border-zinc-900 py-1 last:border-0"
                        >
                            <span className="text-zinc-500">
                                {moment(command.createdAt).format(
                                    'MM/DD HH:mm:ss',
                                )}
                            </span>{' '}
                            <span
                                className={
                                    HISTORY_STATUS_COLOR[command.status] ||
                                    'text-zinc-400'
                                }
                            >
                                [{command.status}]
                            </span>{' '}
                            <span className="text-zinc-100">
                                {command.commandType}
                            </span>
                            {command.createdByUsername && (
                                <span className="text-zinc-500">
                                    {' '}
                                    by {command.createdByUsername}
                                </span>
                            )}
                            {detail && (
                                <span className="text-zinc-400"> {detail}</span>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    </Panel>
);

export default RemoteControlView;
